import React, { useCallback, useState, useEffect } from 'react';
import { useField } from '@unform/core';
import { Box, Text, VStack, HStack, Icon, useToast } from '@chakra-ui/react';
import { RiUploadLine, RiFileTextLine, RiDeleteBinLine } from 'react-icons/ri';

interface IUploadSimulatedProps {
  name: string;
}

export function UploadSimulated({ name }: IUploadSimulatedProps) {
  const { fieldName, registerField, error } = useField(name);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const toast = useToast();

  // Registrar campo no Unform - armazena o File real
  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => uploadedFile,
      setValue: (_, value) => setUploadedFile(value),
      clearValue: () => setUploadedFile(null),
    });
  }, [fieldName, registerField, uploadedFile]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (file) {
        // Validar tipo de arquivo
        const allowedTypes = [
          'image/jpeg',
          'image/png',
          'image/jpg',
          'application/pdf',
        ];
        if (!allowedTypes.includes(file.type)) {
          toast({
            title: 'Tipo de arquivo não permitido',
            description: 'Apenas imagens (JPG, PNG) e PDF são aceitos',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        // Validar tamanho (máx 5MB)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
          toast({
            title: 'Arquivo muito grande',
            description: 'O arquivo deve ter no máximo 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        // Armazenar o File real (necessário para upload multipart)
        setUploadedFile(file);
      }
    },
    [toast],
  );

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box width="100%" mb={2}>
      {!uploadedFile ? (
        <Box
          as="label"
          htmlFor={`upload-${fieldName}`}
          borderWidth={2}
          borderStyle="dashed"
          borderColor={error ? 'red.500' : 'gray.600'}
          borderRadius={8}
          p={6}
          cursor="pointer"
          _hover={{ borderColor: 'blue.500', bg: 'gray.700' }}
          transition="all 0.2s"
          display="block"
          width="100%"
        >
          <VStack spacing={2}>
            <Icon as={RiUploadLine} fontSize="3xl" color="gray.400" />
            <Text color="gray.300" fontSize="sm" textAlign="center">
              Clique ou arraste o comprovante aqui
            </Text>
            <Text color="gray.500" fontSize="xs" textAlign="center">
              JPG, PNG ou PDF até 5MB
            </Text>
          </VStack>
          <input
            id={`upload-${fieldName}`}
            type="file"
            accept="image/jpeg,image/png,image/jpg,application/pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>
      ) : (
        <Box
          borderWidth={1}
          borderColor="green.500"
          borderRadius={8}
          p={4}
          bg="gray.700"
        >
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Icon as={RiFileTextLine} fontSize="2xl" color="green.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.100">
                  {uploadedFile.name}
                </Text>
                <Text fontSize="xs" color="gray.400">
                  {formatFileSize(uploadedFile.size)}
                </Text>
              </VStack>
            </HStack>
            <Box
              as="button"
              type="button"
              onClick={handleRemoveFile}
              p={2}
              borderRadius={4}
              _hover={{ bg: 'red.700' }}
              transition="all 0.2s"
            >
              <Icon as={RiDeleteBinLine} fontSize="lg" color="red.400" />
            </Box>
          </HStack>
        </Box>
      )}

      {error && (
        <Text color="red.500" fontSize="xs" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  );
}
